import { useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../../../../shared/ui/button/Button";
import FilterSelect from "../../../../shared/ui/form/FilterSelect";
import Input from "../../../../shared/ui/form/input/InputField";
import Label from "../../../../shared/ui/form/Label";
import { useCenterMembers, useCenterPatients, useCourseLabels, useCreateCenterCouple } from "../../../center";
import { STUDENT_ERRORS } from "../refusals";

const SELECT_CLASS =
    "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm dark:border-gray-700";

/** The most patients of one therapist offered at once. */
const CANDIDATES_LIMIT = 500;

/**
 * A couple of two patients the centre already has, of one of its therapists.
 *
 * As a therapist makes a couple of their own patients: nobody new is taken on
 * here, and both partners are the same therapist's.
 */
export default function CoupleFromPatientsForm({ centerId, onClose }: { centerId: string; onClose: () => void }) {
    const { t } = useTranslation();
    const { members } = useCenterMembers(centerId, true);
    const therapists = members.filter((member) => member.roles.includes("therapist"));
    const create = useCreateCenterCouple(centerId);

    const [therapistId, setTherapistId] = useState("");
    const [first, setFirst] = useState("");
    const [second, setSecond] = useState("");
    const [name, setName] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState(false);
    // A training centre's student is named with the course they are on.
    const { labelOf } = useCourseLabels();

    const { patients } = useCenterPatients(therapistId ? centerId : undefined, {
        therapist_id: therapistId || undefined,
        limit: CANDIDATES_LIMIT,
    });
    const options = (exclude: string) =>
        patients
            .filter((patient) => !patient.is_anonymous && patient.patient_id !== exclude)
            .map((patient) => ({
                value: patient.patient_id,
                label: labelOf(patient),
                subLabel: patient.contact_email ?? undefined,
            }));

    const partner = (n: number, value: string, set: (id: string) => void, other: string) => (
        <div className="min-w-0">
            <Label>{t("center.students.couplePartner", { n })}</Label>
            <FilterSelect
                options={options(other)}
                value={value}
                onChange={(picked) => set((picked as string) || "")}
                placeholder={t("center.students.choosePatient")}
                searchPlaceholder={t("patient.searchPlaceholder")}
                className="border-border-strong bg-transparent text-sm"
            />
        </div>
    );

    const invalid = !therapistId || !first || !second || first === second;

    const handleSubmit = async () => {
        setSubmitted(true);
        setError(null);
        setDone(false);
        if (invalid) return;
        try {
            await create.mutateAsync({
                therapist_id: therapistId,
                patient_ids: [first, second],
                display_name: name.trim() || null,
            });
            setDone(true);
            setFirst("");
            setSecond("");
            setName("");
            setSubmitted(false);
        } catch (err: unknown) {
            const code = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
            setError(t(STUDENT_ERRORS[code ?? ""] ?? "center.students.errors.generic"));
        }
    };

    return (
        <form
            noValidate
            className="flex flex-col gap-5"
            onSubmit={(e) => {
                e.preventDefault();
                void handleSubmit();
            }}
        >
            <p className="text-sm text-content-tertiary">{t("center.students.coupleHint")}</p>
            <div className="grid gap-4 sm:grid-cols-2">
                <div>
                    <Label htmlFor="couple-therapist">
                        {t("center.students.mentor")}
                        <span className="text-red-500"> *</span>
                    </Label>
                    <select
                        id="couple-therapist"
                        value={therapistId}
                        onChange={(e) => {
                            setTherapistId(e.target.value);
                            // Another therapist's patients cannot be this couple.
                            setFirst("");
                            setSecond("");
                        }}
                        aria-invalid={submitted && !therapistId}
                        className={`${SELECT_CLASS} ${submitted && !therapistId ? "border-red-500" : ""}`}
                    >
                        <option value="">{t("center.students.chooseMentor")}</option>
                        {therapists.map((therapist) => (
                            <option key={therapist.therapist_id} value={therapist.therapist_id}>
                                {`${therapist.first_name} ${therapist.last_name}`}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <Label htmlFor="couple-name">{t("center.students.coupleNameOptional")}</Label>
                    <Input
                        id="couple-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t("center.students.coupleNamePlaceholder")}
                    />
                </div>
            </div>

            {therapistId ? (
                <div className="grid gap-4 sm:grid-cols-2">
                    {partner(1, first, setFirst, second)}
                    {partner(2, second, setSecond, first)}
                </div>
            ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">{t("center.students.chooseMentorFirst")}</p>
            )}

            {submitted && invalid && (
                <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                    {t("center.students.coupleErrorForm")}
                </p>
            )}
            {error && (
                <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                    {error}
                </p>
            )}
            {done && (
                <p role="status" className="text-sm text-brand-700 dark:text-brand-300">
                    {t("center.students.coupleCreated")}
                </p>
            )}

            <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={onClose}>
                    {t("common.close")}
                </Button>
                <Button type="submit" loading={create.isPending} disabled={create.isPending}>
                    {t("center.students.submitCouple")}
                </Button>
            </div>
        </form>
    );
}
